/**
 * One dialog, two purposes (the SharedFolderDialog pattern): the link wizard
 * (connect -> server -> channel -> backfill -> done, with the OAuth redirect
 * resuming at the server step) and per-link whitelist management. Openable
 * preselected from the tree context menu and the dashboard.
 */
import { useEffect, useMemo, useState } from "react";
import { create } from "zustand";
import * as Dialog from "@radix-ui/react-dialog";
import { ArrowLeft, Check, ExternalLink, Hash, ShieldAlert } from "lucide-react";
import type { DiscordLinkDto } from "@hydra-notes/shared";
import { useAllSpaceItems } from "@/data/store";
import { toast } from "@/ui/toast";
import { Button } from "@/ui/Button";
import { stashWizardState, useDiscord, type WizardChannel, type WizardGuild } from "./useDiscordStore";
import { WhitelistManager } from "./WhitelistManager";
import s from "./Discord.module.css";

type View =
  | { kind: "wizard"; listId?: string; spaceId?: string }
  | { kind: "manage"; link: DiscordLinkDto };

interface DialogState {
  open: boolean;
  view: View | null;
  openWizard: (preselect?: { listId: string; spaceId?: string }) => void;
  openManage: (link: DiscordLinkDto) => void;
  /** Route by state: manage an existing board, or link a new one. */
  openForList: (preselect: { listId: string; spaceId?: string }) => Promise<void>;
  setOpen: (open: boolean) => void;
}

export const useDiscordLinkDialog = create<DialogState>((set) => ({
  open: false,
  view: null,
  openWizard: (preselect) => set({ open: true, view: { kind: "wizard", ...preselect } }),
  openManage: (link) => set({ open: true, view: { kind: "manage", link } }),
  openForList: async (preselect) => {
    // links may never have been fetched in this session (the dashboard is what
    // usually loads them), so ask before deciding which view to show
    await useDiscord.getState().refreshLinks();
    const link = useDiscord.getState().links.find((l) => l.listId === preselect.listId);
    if (link) set({ open: true, view: { kind: "manage", link } });
    else set({ open: true, view: { kind: "wizard", ...preselect } });
  },
  setOpen: (open) => set((st) => ({ open, view: open ? st.view : null })),
}));

export function DiscordLinkDialog() {
  const { open, view, setOpen } = useDiscordLinkDialog();
  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className={s.overlay} />
        <Dialog.Content className={s.dialog} aria-describedby={undefined}>
          {view?.kind === "wizard" ? <Wizard preListId={view.listId} preSpaceId={view.spaceId} /> : null}
          {view?.kind === "manage" ? <Manage linkId={view.link.id} /> : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

type Step = "connect" | "server" | "channel" | "backfill" | "done";

function Wizard({ preListId, preSpaceId }: { preListId?: string; preSpaceId?: string }) {
  const account = useDiscord((st) => st.account);
  const spaces = useAllSpaceItems();
  const [step, setStep] = useState<Step>("connect");
  const [listId, setListId] = useState(preListId ?? "");
  const [spaceId, setSpaceId] = useState(preSpaceId ?? "");
  const [guilds, setGuilds] = useState<WizardGuild[] | null>(null);
  const [guildId, setGuildId] = useState("");
  const [channels, setChannels] = useState<WizardChannel[] | null>(null);
  const [channelId, setChannelId] = useState("");
  const [backfill, setBackfill] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<DiscordLinkDto | null>(null);

  const taskLists = useMemo(
    () =>
      spaces.flatMap((sp) =>
        sp.items
          .filter((i) => i.type === "tasklist" && !i.trashedAt && !i.archivedAt)
          .map((i) => ({ id: i.id, spaceId: sp.spaceId, title: i.title || "Untitled", shared: sp.kind === "shared" })),
      ),
    [spaces],
  );

  // connected already: jump straight to servers
  useEffect(() => {
    void useDiscord.getState().refreshAccount();
  }, []);
  useEffect(() => {
    if (step === "connect" && account?.connected) {
      setStep("server");
    }
  }, [account, step]);
  useEffect(() => {
    if (step !== "server" || guilds) return;
    void useDiscord
      .getState()
      .fetchGuilds()
      .then((res) => {
        if ("guilds" in res) setGuilds(res.guilds);
        else if (res.reconnect) setError("reconnect");
        else setError(res.error);
      });
  }, [step, guilds]);

  const connect = () => {
    stashWizardState({ listId, spaceId });
    window.location.href = "/api/discord/oauth/start";
  };

  const pickGuild = async (g: WizardGuild) => {
    if (!g.botPresent) return;
    setGuildId(g.id);
    setChannels(null);
    setStep("channel");
    const res = await useDiscord.getState().fetchChannels(g.id);
    if ("channels" in res) setChannels(res.channels);
    else setError(res.error);
  };

  const submit = async () => {
    if (!listId || !guildId || !channelId) return;
    setBusy(true);
    setError(null);
    const res = await useDiscord.getState().createLink({
      listId,
      ...(spaceId ? { spaceId } : {}),
      guildId,
      channelId,
      backfill,
    });
    setBusy(false);
    if ("link" in res) {
      if (res.warning) toast.info(res.warning);
      setCreated(res.link);
      setStep("done");
    } else if (res.reconnect) {
      setError("reconnect");
    } else {
      setError(res.error);
    }
  };

  if (error === "reconnect") {
    return (
      <>
        <Dialog.Title className={s.title}>Reconnect Discord</Dialog.Title>
        <p className={s.hint}>Your Discord session expired. Reconnect to keep going; you will land right back here.</p>
        <Button variant="solid" onClick={connect} className={s.connectBtn}>
          Reconnect Discord
        </Button>
      </>
    );
  }

  return (
    <>
      <Dialog.Title className={s.title}>
        {step === "done" ? "Channel linked" : "Link a task list to Discord"}
      </Dialog.Title>

      {step !== "connect" && step !== "done" ? (
        <div className={s.listPickerRow}>
          <span className={s.hint}>Task list:</span>
          <select
            className={s.select}
            value={`${spaceId}/${listId}`}
            onChange={(e) => {
              const [sp, li] = e.target.value.split("/");
              setSpaceId(sp === "personal" ? "" : sp!);
              setListId(li ?? "");
            }}
          >
            <option value="/">Pick a list...</option>
            {taskLists.map((l) => (
              <option key={l.id} value={`${l.shared ? l.spaceId : "personal"}/${l.id}`}>
                {l.title}
                {l.shared ? " (shared)" : ""}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {step === "connect" ? (
        <>
          <p className={s.hint}>
            Connect your Discord account first. We only read who you are and which servers you can manage; nothing is
            posted on your behalf and no tokens are stored.
          </p>
          <Button variant="solid" onClick={connect} className={s.connectBtn}>
            Connect Discord
          </Button>
        </>
      ) : null}

      {step === "server" ? (
        <>
          <p className={s.hint}>Servers where you have Manage Server:</p>
          {!guilds ? (
            <p className={s.hint}>Loading your servers...</p>
          ) : guilds.length === 0 ? (
            <p className={s.hint}>No servers found where you can Manage Server.</p>
          ) : (
            <div className={s.guildGrid}>
              {guilds.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  className={[s.guildCard, !g.botPresent ? s.guildCardDim : ""].join(" ")}
                  onClick={() => void pickGuild(g)}
                >
                  {g.iconUrl ? <img src={g.iconUrl} alt="" className={s.guildIcon} /> : <span className={s.guildIconFallback} />}
                  <span className={s.guildName}>{g.name}</span>
                  {!g.botPresent && g.inviteUrl ? (
                    <a
                      href={g.inviteUrl}
                      target="_blank"
                      rel="noreferrer"
                      className={s.inviteLink}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Invite the bot <ExternalLink size={11} />
                    </a>
                  ) : null}
                </button>
              ))}
            </div>
          )}
        </>
      ) : null}

      {step === "channel" ? (
        <>
          <button type="button" className={s.backBtn} onClick={() => setStep("server")}>
            <ArrowLeft size={13} /> Servers
          </button>
          <p className={s.hint}>
            Pick the channel that becomes the task board. We advise a new private channel dedicated to it: every human
            message posted there gets removed.
          </p>
          {!channels ? (
            <p className={s.hint}>Loading channels...</p>
          ) : (
            <div className={s.channelList}>
              {channels.map((c) => {
                const blocked = c.missingPerms.length > 0 || c.linked;
                return (
                  <button
                    key={c.id}
                    type="button"
                    disabled={blocked}
                    className={[s.channelRow, channelId === c.id ? s.channelRowActive : ""].join(" ")}
                    onClick={() => setChannelId(c.id)}
                  >
                    <Hash size={13} />
                    <span className={s.channelName}>{c.name}</span>
                    {c.linked ? (
                      <span className={s.channelWarn}>already a board</span>
                    ) : c.missingPerms.length ? (
                      <span className={s.channelWarn}>
                        <ShieldAlert size={11} /> bot missing: {c.missingPerms.join(", ")}
                      </span>
                    ) : channelId === c.id ? (
                      <Check size={13} />
                    ) : null}
                  </button>
                );
              })}
            </div>
          )}
          <div className={s.actions}>
            <Button variant="solid" size="sm" disabled={!channelId || !listId} onClick={() => setStep("backfill")}>
              Continue
            </Button>
          </div>
        </>
      ) : null}

      {step === "backfill" ? (
        <>
          <button type="button" className={s.backBtn} onClick={() => setStep("channel")}>
            <ArrowLeft size={13} /> Channels
          </button>
          <p className={s.hint}>Should the tasks already on this list appear in the channel?</p>
          <label className={s.radioRow}>
            <input type="radio" checked={backfill} onChange={() => setBackfill(true)} />
            <span>
              <strong>Add all current open tasks</strong>
              <br />
              Every open task gets a card now (paced, about one per second).
            </span>
          </label>
          <label className={s.radioRow}>
            <input type="radio" checked={!backfill} onChange={() => setBackfill(false)} />
            <span>
              <strong>Only new tasks from now on</strong>
              <br />
              Existing tasks stay out of Discord forever; everything created from now on syncs.
            </span>
          </label>
          {error ? <p className={s.error}>{error}</p> : null}
          <div className={s.actions}>
            <Button variant="solid" size="sm" disabled={busy || !listId} onClick={() => void submit()}>
              {busy ? "Linking..." : "Link channel"}
            </Button>
          </div>
        </>
      ) : null}

      {step === "done" && created ? (
        <>
          <p className={s.hint}>
            #{created.channelName} in {created.guildName} is now the board for "{created.taskListName}". By default only
            you can add tasks and press buttons there; whitelist people or roles below, or close this and do it later.
          </p>
          <WhitelistManager link={created} />
        </>
      ) : null}
    </>
  );
}

/**
 * Settings for a board that already exists. Reads the link out of the store by
 * id rather than closing over the one it was opened with, so a toggle or a
 * re-verify shows its result here instead of going stale.
 */
function Manage({ linkId }: { linkId: string }) {
  const link = useDiscord((st) => st.links.find((l) => l.id === linkId));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void useDiscord.getState().refreshLinks();
  }, []);

  if (!link) {
    return (
      <>
        <Dialog.Title className={s.title}>Discord board</Dialog.Title>
        <p className={s.hint}>That link is gone. It may have been unlinked somewhere else.</p>
      </>
    );
  }

  return (
    <>
      <Dialog.Title className={s.title}>
        <Hash size={14} /> {link.channelName || link.channelId}
      </Dialog.Title>
      <p className={s.hint}>
        {link.guildName}, showing tasks from {link.taskListName}.
      </p>

      {link.status === "ok" ? (
        <p className={s.statusOk}>
          <Check size={13} /> Working normally
        </p>
      ) : (
        <p className={s.error}>
          <ShieldAlert size={13} /> {link.statusReason ?? "Something is wrong with this link"}
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void useDiscord
                .getState()
                .verifyLink(link.id)
                .finally(() => setBusy(false));
            }}
          >
            Re-verify
          </Button>
        </p>
      )}

      <div className={s.settingRow}>
        <button
          type="button"
          className={[s.toggle, link.buttonsEnabled ? s.toggleOn : ""].join(" ")}
          aria-label={link.buttonsEnabled ? "Hide the buttons on cards" : "Show the buttons on cards"}
          onClick={() => void useDiscord.getState().patchLink(link.id, { buttonsEnabled: !link.buttonsEnabled })}
        >
          <span className={s.toggleKnob} />
        </button>
        <span className={s.settingBody}>
          <span className={s.settingName}>Buttons on cards</span>
          <span className={s.settingHint}>
            Done, Edit, Due and Delete under every task. Turning this off re-renders every card as a plain board.
          </span>
        </span>
      </div>

      <div className={s.settingBlock}>
        <span className={s.settingName}>Who can use this board</span>
        <span className={s.settingHint}>
          Everyone here can add tasks by posting and press the buttons. With nobody listed, that is you alone.
        </span>
        <WhitelistManager link={link} />
      </div>
    </>
  );
}
